import clsx from "clsx";
import { useLayoutEffect, useState } from "react";
import { VirtuosoGrid } from "react-virtuoso";

import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";

import {
  AUDIENCE_PROFILE_STATES,
  CANCELLED_TARGET_STATES,
} from "../../../../constants";
import ProfileCard from "../../ProfileCard";

import type { Dispatch, JSX, SetStateAction } from "react";
import type { AudienceProfile, CancelledTarget } from "../../../../types";

interface AudienceListProps {
  audienceWithCancellation: (AudienceProfile & {
    cancellationDetails: CancelledTarget;
  })[];
  isLoading: boolean;
  selectedProfiles: string[];
  setSelectedProfiles: Dispatch<SetStateAction<string[]>>;
  openLeadPreview: (leadId: string) => void;
  isPreviewOpen: boolean;
}

export default function AudienceList({
  audienceWithCancellation,
  isLoading,
  selectedProfiles,
  setSelectedProfiles,
  openLeadPreview,
  isPreviewOpen,
}: AudienceListProps) {
  //using a state for the scroll container fixes https://linear.app/constel/issue/ENG-6252/ivanadjekic679698ba-3d84-4286-9208-8e83e5ca1daa
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  //use layout effect is preferred here, it eliminates content flashing
  useLayoutEffect(() => {
    const contentScroll = document.getElementById("content-scroll");
    if (contentScroll) {
      setScrollParent(contentScroll);
    }
  }, []);

  return (
    <VirtuosoGrid
      customScrollParent={scrollParent}
      totalCount={audienceWithCancellation.length}
      listClassName={clsx(
        "grid gap-3",
        isPreviewOpen
          ? "grid-cols-1 xl:grid-cols-2"
          : "grid-cols-2 xl:grid-cols-3",
      )}
      itemClassName="flex"
      itemContent={(index) => {
        const profile = audienceWithCancellation[index];
        const isSelected = selectedProfiles.includes(profile.urn);

        function onSelect() {
          if (isSelected) {
            setSelectedProfiles(
              selectedProfiles.filter(
                (selectedProfile) => selectedProfile !== profile.urn,
              ),
            );
          } else {
            setSelectedProfiles([...selectedProfiles, profile.urn]);
          }
        }

        let tagComponent: JSX.Element;
        if (isLoading) {
          tagComponent = <Skeleton className="ml-auto h-6 w-20" />;
        } else if (profile.state) {
          const { tagVariant, label } = AUDIENCE_PROFILE_STATES.find(
            ({ states }) => states.includes(profile.state),
          );

          tagComponent = (
            <Tag variant={tagVariant} className="ml-auto">
              {/* first letter doesn't work on flex elements, this wrapper is necessary */}
              <span className="first-letter:capitalize">
                {profile.cancellationDetails
                  ? CANCELLED_TARGET_STATES[
                      profile.cancellationDetails.transition
                    ]
                  : label}
              </span>
            </Tag>
          );
        }

        return (
          <ProfileCard
            profile={profile}
            isLoading={isLoading}
            isSelected={isSelected}
            onSelect={onSelect}
            openPreview={openLeadPreview}
          >
            {tagComponent}
          </ProfileCard>
        );
      }}
    />
  );
}
